import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { WORDS } from "../../data/words";

interface QuickSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickSearchModal({ isOpen, onClose }: QuickSearchModalProps) {
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return WORDS.filter(
      (w) =>
        w.word.toLowerCase().includes(q) ||
        w.zh.includes(q) ||
        (w.en && w.en.toLowerCase().includes(q))
    ).slice(0, 8);
  }, [query]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-search-title"
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-slate-950/40 backdrop-blur-xs"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <svg className="w-5 h-5 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            id="quick-search-title"
            type="text"
            autoFocus
            placeholder="搜尋單詞或釋義..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 text-[15px] bg-transparent outline-none text-slate-800 placeholder:text-slate-400 font-medium"
          />
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 hover:text-slate-800 cursor-pointer"
          >
            ESC
          </button>
        </div>

        <div className="max-h-[360px] overflow-y-auto p-2">
          {query.trim() === "" ? (
            <div className="p-6 text-center text-slate-400 text-xs font-grotesk">
              輸入單詞開始搜尋（例如：abandon, resilient）
            </div>
          ) : results.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-xs">
              找不到相符單詞
            </div>
          ) : (
            <div className="space-y-1">
              {results.map((word) => (
                <Link
                  key={word.id}
                  to={`/word/${word.id}`}
                  onClick={onClose}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 no-underline transition-colors group"
                >
                  <div>
                    <span className="font-bold text-[15px] text-slate-900 group-hover:text-blue-600">
                      {word.word}
                    </span>
                    <span className="text-xs text-slate-400 ml-2 font-mono">
                      {word.pos}
                    </span>
                    {word.phonetic && (
                      <span className="text-xs text-slate-400 ml-1.5 font-phon">
                        /{word.phonetic}/
                      </span>
                    )}
                    <div className="text-xs text-slate-500 mt-0.5 truncate max-w-sm">
                      {word.zh}
                    </div>
                  </div>
                  <span className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all text-xs">
                    ›
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
